import { useState, useEffect, useRef } from 'react'
import { Wifi, WifiOff, Check, AlertCircle } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

type SyncStatus = 'synced' | 'syncing' | 'offline' | 'error'

interface SyncStatusIndicatorProps {
    status: SyncStatus
    message?: string
}

export function SyncStatusIndicator({ status, message }: SyncStatusIndicatorProps) {
    const [show, setShow] = useState(false)

    const isFirstRender = useRef(true)
    const syncingTimerRef = useRef<number | null>(null)

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false
            // Se começar offline ou error, mostra. Caso contrário, ignora.
            if (status === 'synced' || status === 'syncing') {
                return
            }
        }

        // Limpar timer anterior se existir
        if (syncingTimerRef.current) {
            clearTimeout(syncingTimerRef.current)
            syncingTimerRef.current = null
        }

        // Para estados críticos (offline, error), mostra imediatamente (mas de forma assíncrona)
        if (status === 'offline' || status === 'error') {
            syncingTimerRef.current = setTimeout(() => {
                setShow(true)
            }, 0)
            return () => {
                if (syncingTimerRef.current) {
                    clearTimeout(syncingTimerRef.current)
                    syncingTimerRef.current = null
                }
            }
        }

        // Para 'syncing', só mostra se durar mais de 300ms
        if (status === 'syncing') {
            syncingTimerRef.current = setTimeout(() => {
                setShow(true)
            }, 300)
            return () => {
                if (syncingTimerRef.current) {
                    clearTimeout(syncingTimerRef.current)
                    syncingTimerRef.current = null
                }
            }
        }

        // Para 'synced', só mostra se estava mostrando antes
        if (status === 'synced' && show) {
            syncingTimerRef.current = setTimeout(() => {
                setShow(false)
            }, 2000)
            return () => {
                if (syncingTimerRef.current) {
                    clearTimeout(syncingTimerRef.current)
                    syncingTimerRef.current = null
                }
            }
        }
    }, [status, show])

    const config = {
        synced: {
            icon: Check,
            color: 'text-green-600 dark:text-green-400',
            bg: 'bg-green-50 dark:bg-green-900/20',
            border: 'border-green-200 dark:border-green-800',
            text: message || 'Sincronizado'
        },
        syncing: {
            icon: Wifi,
            color: 'text-blue-600 dark:text-blue-400',
            bg: 'bg-blue-50 dark:bg-blue-900/20',
            border: 'border-blue-200 dark:border-blue-800',
            text: message || 'Sincronizando...'
        },
        offline: {
            icon: WifiOff,
            color: 'text-gray-600 dark:text-gray-400',
            bg: 'bg-gray-50 dark:bg-gray-900/20',
            border: 'border-gray-200 dark:border-gray-800',
            text: message || 'Offline'
        },
        error: {
            icon: AlertCircle,
            color: 'text-red-600 dark:text-red-400',
            bg: 'bg-red-50 dark:bg-red-900/20',
            border: 'border-red-200 dark:border-red-800',
            text: message || 'Erro ao sincronizar'
        }
    }

    const current = config[status]
    const Icon = current.icon

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    {...({
                        initial: { opacity: 0, y: -10 },
                        animate: { opacity: 1, y: 0 },
                        exit: { opacity: 0, y: -10 }
                    } as any)}
                    transition={{ duration: 0.2 }}
                    className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-3 py-2 rounded-lg border ${current.bg} ${current.border} shadow-lg`}
                >
                    <Icon className={`w-4 h-4 ${current.color} ${status === 'syncing' ? 'animate-pulse' : ''}`} />
                    <span className={`text-sm font-medium ${current.color}`}>
                        {current.text}
                    </span>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

// Hook para gerenciar status de sincronização
// eslint-disable-next-line react-refresh/only-export-components
export function useSyncStatus() {
    const [status, setStatus] = useState<SyncStatus>(() =>
        navigator.onLine ? 'synced' : 'offline'
    )
    const [message, setMessage] = useState<string>()

    const updateStatus = (newStatus: SyncStatus, newMessage?: string) => {
        setStatus(newStatus)
        setMessage(newMessage)
    }

    // Detectar mudanças de status offline/online
    useEffect(() => {
        const handleOnline = () => updateStatus('synced', 'Conexão restaurada')
        const handleOffline = () => updateStatus('offline', 'Sem conexão')

        window.addEventListener('online', handleOnline)
        window.addEventListener('offline', handleOffline)

        return () => {
            window.removeEventListener('online', handleOnline)
            window.removeEventListener('offline', handleOffline)
        }
    }, [])

    return { status, message, updateStatus }
}
